import React, { useMemo } from 'react';
import DateFilter from 'src/mtska-frontend-app-component/components/List/customSelectionFilter/DateFilter/DateFilter';
import { InputAutocomplete, InputFilterMultiCheckbox } from 'src/mtska-frontend-app-component';

/** Campo nel filterModel per l’area (stesso valore usato da Area / Shop / Employment). */
export const DASHBOARD_RELATED_AREA_NAME_FIELD = '_related_area_name';
export const DASHBOARD_RELATED_ACCOUNT_AREA_NAME_FIELD = '_related_account_area_name';

/** Campo nel filterModel per gli shop selezionati nel filtro Shop. */
export const DASHBOARD_RELATED_SHOP_NAME_FIELD = '_related_shop_name';

/** Campo su `persons/employment` per restringere per nome shop (testo aggregato). */
export const EMPLOYMENT_SHOPS_READABLE_FIELD = '_related_shops_readable';

export const AGENT_EMPLOYMENT_FILTERS = {
    items: [
        { field: 'role', operator: 'equals', value: 'Agent' },
        { field: 'terminated_at_readable', operator: 'isEmpty', value: '_' },
    ],
};

const FILTER_STYLE = { flex: '1' };
const FILTER_SIZE = 'small';

const getFilterItem = (filterModel, field) =>
    filterModel?.items?.find((item) => item.field === field);

const getUserRoles = (user) => user?.data?.roles ?? [];

export const filterDefinitionVisibleForUser = (definition, user) => {
    if (!definition || typeof definition !== 'object') return true;
    if (!user) return true;
    const roles = getUserRoles(user);
    if (definition.excludedRoles?.length && definition.excludedRoles.some((r) => roles.includes(r))) {
        return false;
    }
    if (definition.requiredRoles?.length && !definition.requiredRoles.some((r) => roles.includes(r))) {
        return false;
    }
    return true;
};

const optionVisibleForUser = (option, user) => {
    if (!option || typeof option !== 'object') return true;
    if (!user) return true;
    const roles = getUserRoles(user);
    if (option.excludedRoles?.length && option.excludedRoles.some((r) => roles.includes(r))) {
        return false;
    }
    if (option.requiredRoles?.length && !option.requiredRoles.some((r) => roles.includes(r))) {
        return false;
    }
    return true;
};

const getNormalizedFilterValues = (filterModel, field) => {
    const filterItem = getFilterItem(filterModel, field);
    const raw = filterItem?.value;
    if (raw == null || raw === '') return null;
    const values =
        filterItem?.operator === 'in' && Array.isArray(raw) ? raw : [raw];
    const normalized = values.filter((value) => value != null && value !== '');
    return normalized.length > 0 ? normalized : null;
};

const getMultiCheckboxValue = (filterModel, field, valueKey, multi = true) => {
    const values = getNormalizedFilterValues(filterModel, field);
    if (!values) return null;
    return { [valueKey]: multi ? values : values[0] };
};

const stableKey = (value) => {
    if (value == null) return value;
    if (typeof value !== 'object') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const keyEquals = (a, b) => stableKey(a) === stableKey(b);

const buildItemsFilter = (field, values, operator = 'in') => {
    if (!Array.isArray(values) || values.length === 0) return undefined;
    if (operator !== 'in') {
        return {
            items: values.map((value) => ({
                field,
                operator,
                value,
            })),
        };
    }
    return {
        items: [
            {
                field,
                operator,
                value: values,
            },
        ],
    };
};

const getSelectedAreaNamesByField = (filterModel, areaField) =>
    getNormalizedFilterValues(filterModel, areaField);

const getSelectedAreaNamesForRestriction = (filterModel, preferredField) => {
    const primary = preferredField
        ? getSelectedAreaNamesByField(filterModel, preferredField)
        : null;
    if (primary?.length) return primary;

    const accountAreas = getSelectedAreaNamesByField(
        filterModel,
        DASHBOARD_RELATED_ACCOUNT_AREA_NAME_FIELD,
    );
    if (accountAreas?.length) return accountAreas;

    return getSelectedAreaNamesByField(filterModel, DASHBOARD_RELATED_AREA_NAME_FIELD);
};

/** Filtri API per liste server (shop, employment, …) quando in dashboard è selezionata almeno un’area. */
const buildAreaRestrictionFilter = (filterModel, areaField = DASHBOARD_RELATED_AREA_NAME_FIELD) => {
    const selectedAreas = getSelectedAreaNamesForRestriction(filterModel, areaField);
    return buildItemsFilter(areaField, selectedAreas);
};

const buildRestrictByFieldFilter = (filterModel, restrictByField) => {
    if (!restrictByField) return undefined;

    if (typeof restrictByField === 'string') {
        const values = getNormalizedFilterValues(filterModel, restrictByField);
        return buildItemsFilter(restrictByField, values, 'in');
    }

    if (typeof restrictByField === 'object') {
        const sourceField = restrictByField.sourceField || restrictByField.from || restrictByField.field;
        if (!sourceField) return undefined;
        const targetField = restrictByField.targetField || restrictByField.to || sourceField;
        const operator = restrictByField.operator || 'in';
        const values = getNormalizedFilterValues(filterModel, sourceField);
        return buildItemsFilter(targetField, values, operator);
    }

    return undefined;
};

const getSelectedShopNames = (filterModel) =>
    getNormalizedFilterValues(filterModel, DASHBOARD_RELATED_SHOP_NAME_FIELD);

/**
 * Vincolo employment per shop scelti nel filtro Shop: `_related_shops_readable` contains per ogni valore.
 * (Allineato a RentalDashboard su persons/employment.)
 */
const buildShopReadableContainsFilters = (filterModel) => {
    const shops = getSelectedShopNames(filterModel);
    if (!shops?.length) return undefined;
    return {
        items: shops.map((value) => ({
            field: EMPLOYMENT_SHOPS_READABLE_FIELD,
            operator: 'contains',
            value,
        })),
    };
};

/** Unisce `{ items }` base con filtri aggiuntivi (es. vincolo area); se `additional` è assente resta `base`. */
const mergeServerFilterItems = (base, additional) => {
    if (!additional?.items?.length) return base;
    return {
        items: [...(base.items ?? []), ...additional.items],
    };
};

const readSelectionValues = (selection, valueKey) => {
    if (!selection) return [];
    const raw = selection[valueKey];
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw;
    return [raw];
};

const applyMultiCheckboxSelection = ({
    addFilter,
    field,
    selection,
    valueKey,
    multi = true,
    operator,
}) => {
    const resolvedOperator = operator || (multi ? 'in' : 'equals');
    if (selection == null) {
        addFilter(field, null);
        return;
    }

    if (multi) {
        const values = readSelectionValues(selection, valueKey);
        addFilter(field, values, resolvedOperator);
        return;
    }

    const raw = selection?.[valueKey];
    const scalarValue = Array.isArray(raw) ? raw[0] : raw;
    addFilter(field, scalarValue ?? null, resolvedOperator);
};

const getTransposedDate = (months) => {
    const today = new Date();
    today.setMonth(today.getMonth() + months);

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
};

const resolveDynamicOptionValue = (option) => {
    if (!option || typeof option !== 'object') return option;
    const valueDynamic = option.valueDynamic;
    if (!valueDynamic || typeof valueDynamic !== 'object') return option;

    if (valueDynamic.type === 'relativeDate') {
        const months = Number(valueDynamic.months ?? 0);
        return {
            ...option,
            value: getTransposedDate(months),
        };
    }

    if (valueDynamic.type === 'relativeDateRange') {
        const fromMonths = Number(valueDynamic.fromMonths ?? 0);
        const toMonths = Number(valueDynamic.toMonths ?? 0);
        return {
            ...option,
            value: {
                from: getTransposedDate(fromMonths),
                to: getTransposedDate(toMonths),
            },
        };
    }

    return option;
};

const FilterDateRange = ({ filterModel, addFilter, t }) => (
    <DateFilter
        applyValue={(updatedItem) => {
            addFilter('registered_at', updatedItem.value, 'between');
        }}
        item={{
            field: 'registered_at',
            operator: 'between',
            value: getFilterItem(filterModel, 'registered_at')?.value ?? null,
        }}
        label={t('registered_at')}
    />
);

const FilterServerMultiCheckbox = ({
    t,
    filterModel,
    addFilter,
    field,
    dataProvider,
    valueKey,
    labelkey,
    label,
    project,
    distinct,
    filters,
    sourceField,
    multi = true,
    operator,
}) => (
    <InputFilterMultiCheckbox
        dataProvider={dataProvider}
        valueKey={valueKey}
        labelkey={labelkey}
        project={project}
        distinct={distinct}
        filters={filters}
        multi={multi}
        sourceField={sourceField || label}
        value={getMultiCheckboxValue(filterModel, field, valueKey, multi)}
        onChange={(selection) => {
            applyMultiCheckboxSelection({ addFilter, field, selection, valueKey, multi, operator });
        }}
        label={t(label)}
        style={FILTER_STYLE}
        size={FILTER_SIZE}
    />
);

const FilterStaticMultiCheckbox = ({
    t,
    filterModel,
    addFilter,
    field,
    valueKey,
    labelkey = 'label',
    label,
    staticOptions,
    multi = true,
    operator,
}) => {
    const handleChange = (selection) => {
        if (selection == null) {
            addFilter(field, null);
            return;
        }

        if (multi) {
            applyMultiCheckboxSelection({ addFilter, field, selection, valueKey, multi, operator });
            return;
        }

        const raw = selection?.[valueKey];
        const scalarValue = Array.isArray(raw) ? raw[0] : raw;
        const selectedOption = (staticOptions ?? []).find((option) => keyEquals(option?.[valueKey], scalarValue));
        const selectedOperator = selectedOption?.operator || operator || 'equals';
        addFilter(field, scalarValue ?? null, selectedOperator);
    };

    return (
        <InputFilterMultiCheckbox
            staticOptions={staticOptions}
            valueKey={valueKey}
            labelkey={labelkey}
            multi={multi}
            value={getMultiCheckboxValue(filterModel, field, valueKey, multi)}
            onChange={handleChange}
            label={t(label)}
            style={FILTER_STYLE}
            size={FILTER_SIZE}
        />
    );
};

export const FilterEmployment = ({ t, filterModel, addFilter, field, label }) => {
    const listFilters = useMemo(
        () =>
            mergeServerFilterItems(
                mergeServerFilterItems(
                    AGENT_EMPLOYMENT_FILTERS,
                    buildAreaRestrictionFilter(filterModel),
                ),
                buildShopReadableContainsFilters(filterModel),
            ),
        [filterModel],
    );

    return (
        <FilterServerMultiCheckbox
            t={t}
            filterModel={filterModel}
            addFilter={addFilter}
            field={field}
            label={label}
            dataProvider="persons/employment"
            valueKey="_related_person_fullname_reverse"
            labelkey="_related_person_fullname_reverse"
            project={['_related_person_fullname_reverse']}
            filters={listFilters}
        />
    );
};

const FilterSourceAutocomplete = ({ t, filterModel, addFilter }) => {
    const values = getNormalizedFilterValues(filterModel, '_related_source_name');
    const selected = values?.[0] ?? null;

    return (
        <InputAutocomplete
            type="autocomplete"
            dataProvider="advertisings/source/autocomplete"
            label={t('source')}
            searchkey="name"
            labelkey="name"
            project={['name']}
            value={selected ? { name: selected } : null}
            onChange={(value) => {
                addFilter('_related_source_name', value?.name ?? null);
            }}
            style={FILTER_STYLE}
            size={FILTER_SIZE}
        />
    );
};

const FilterYearStatic = ({ t, filterModel, addFilter, staticOptions }) => {
    const values = getNormalizedFilterValues(filterModel, 'year');
    const normalizedValues = values?.map((value) => String(value)) ?? null;
    const value = normalizedValues?.length ? { year: normalizedValues } : null;

    return (
        <InputFilterMultiCheckbox
            staticOptions={staticOptions}
            valueKey="year"
            labelkey="label"
            value={value}
            onChange={(selection) => {
                if (selection == null) {
                    addFilter('year', null);
                    return;
                }
                const nextValues = readSelectionValues(selection, 'year');
                addFilter('year', nextValues, 'in');
            }}
            label={t('year')}
            style={FILTER_STYLE}
            size={FILTER_SIZE}
        />
    );
};

export const useDashboardFilterStaticPresets = (t) =>
    useMemo(
        () => ({
            statusNewExisting: [
                { status: 'new', label: t('new') },
                { status: 'existing', label: t('existing') },
            ],
            productLead: [
                { product: 'buy', label: t('buy') },
                { product: 'sell', label: t('sell') },
                { product: 'rent_in', label: t('rent_in') },
                { product: 'lease_out', label: t('lease_out') },
                { product: 'referral_out', label: t('referral_out') },
            ],
            productProposal: [
                { product: 'buy', label: t('buy') },
                { product: 'rent', label: t('rent') },
                { product: 'referral_out', label: t('referral_out') },
                { product: 'penalty_fee', label: t('penalty_fee') },
            ],
            actionListing: [
                { action: 'charge', label: t('charge') },
                { action: 'charge_for_takeover', label: t('charge_for_takeover') },
                { action: 'discharge_acceptance', label: t('discharge_acceptance') },
                {
                    action: 'discharge_end_of_agreement',
                    label: t('discharge_end_of_agreement'),
                },
                { action: 'discharge_area_change', label: t('discharge_area_change') },
                { action: 'start', label: t('start') },
            ],
            yearStatic: [
                { year: '2026', label: t('2026') },
                { year: '2025', label: t('2025') },
                { year: '2024', label: t('2024') },
            ],
            legalEntityStatic: [
                { name: 'Elletre', label: t('Elletre') },
                { name: 'Levante', label: t('Levante') },
                { name: 'QuattroK', label: t('QuattroK') },
            ],
        }),
        [t],
    );

/** Opzioni statiche: da JSON (`staticOptions` + `labelKey`) oppure preset legacy (`staticPreset` + hook). */
const resolveConfigurableStaticRows = (definition, t, hookStaticOptions, visibilityUser) => {
    if (Array.isArray(definition.staticOptions) && definition.staticOptions.length > 0) {
        return definition.staticOptions
            .filter((row) => optionVisibleForUser(row, visibilityUser))
            .map((row) => {
            if (row == null || typeof row !== 'object') return row;
            const dynamicRow = resolveDynamicOptionValue(row);
            const { labelKey, labelKeyParams, valueDynamic, requiredRoles, excludedRoles, ...valueFields } = dynamicRow;
            if (labelKey === undefined || labelKey === null) {
                return { ...valueFields };
            }
            return { ...valueFields, label: t(labelKey, labelKeyParams || undefined) };
        });
    }
    if (definition.staticPreset && hookStaticOptions?.[definition.staticPreset]) {
        return hookStaticOptions[definition.staticPreset];
    }
    return undefined;
};

const DashboardConfigurableFilter = ({ definition, t, filterModel, addFilter, staticOptions, visibilityUser }) => {
    const type = definition?.type;
    if (!type) return null;
    const isVisible = filterDefinitionVisibleForUser(definition, visibilityUser);
    if (!isVisible) return null;

    switch (type) {
        case 'dateRange':
            return (
                <DateFilter
                    applyValue={(updatedItem) => {
                        addFilter(definition.field, updatedItem.value, definition.operator || 'between');
                    }}
                    item={{
                        field: definition.field,
                        operator: definition.operator || 'between',
                        value: getFilterItem(filterModel, definition.field)?.value ?? null,
                    }}
                    label={t(definition.labelKey || definition.field)}
                />
            );
        case 'serverMultiCheckbox': {
            let filtersProp = definition.serverFilters;
            const restrictionFilter =
                buildRestrictByFieldFilter(filterModel, definition.restrictByField) ||
                (definition.restrictBySelectedAreas
                    ? buildAreaRestrictionFilter(
                          filterModel,
                          definition.restrictByAreaField || definition.areaFieldForRestriction,
                      )
                    : undefined);
            if (restrictionFilter) {
                filtersProp = mergeServerFilterItems(
                    filtersProp && filtersProp.items?.length ? filtersProp : { items: [] },
                    restrictionFilter,
                );
            }
            if (!filtersProp?.items?.length) {
                filtersProp = undefined;
            }
            return (
                <FilterServerMultiCheckbox
                    t={t}
                    filterModel={filterModel}
                    addFilter={addFilter}
                    field={definition.field}
                    label={definition.labelKey}
                    dataProvider={definition.dataProvider}
                    valueKey={definition.valueKey}
                    labelkey={definition.labelkey || definition.valueKey}
                    project={definition.project}
                    distinct={definition.distinct}
                    filters={filtersProp}
                    sourceField={definition.sourceField}
                    multi={definition.multi !== false}
                    operator={definition.operator}
                />
            );
        }
        case 'employment':
            return (
                <FilterEmployment
                    t={t}
                    filterModel={filterModel}
                    addFilter={addFilter}
                    field={definition.field}
                    label={definition.labelKey}
                />
            );
        case 'staticMultiCheckbox':
            return (
                <FilterStaticMultiCheckbox
                    t={t}
                    filterModel={filterModel}
                    addFilter={addFilter}
                    field={definition.field}
                    valueKey={definition.valueKey}
                    labelkey={definition.labelkey || 'label'}
                    label={definition.labelKey}
                    staticOptions={resolveConfigurableStaticRows(definition, t, staticOptions, visibilityUser)}
                    multi={definition.multi !== false}
                    operator={definition.operator}
                />
            );
        case 'yearStatic':
            return (
                <FilterYearStatic
                    t={t}
                    filterModel={filterModel}
                    addFilter={addFilter}
                    staticOptions={resolveConfigurableStaticRows(definition, t, staticOptions, visibilityUser)}
                />
            );
        case 'sourceAutocomplete':
            return <FilterSourceAutocomplete t={t} filterModel={filterModel} addFilter={addFilter} />;
        case 'inputAutocomplete': {
            const field = definition.field;
            const searchkey = definition.searchKey || field;
            const labelkey = definition.labelKeyField || field;
            const val = getFilterItem(filterModel, field)?.value;
            return (
                <InputAutocomplete
                    type="autocomplete"
                    dataProvider={definition.dataProvider || 'cars/car/autocomplete'}
                    label={t(definition.labelKey || field)}
                    searchkey={searchkey}
                    labelkey={labelkey}
                    project={definition.project || [labelkey]}
                    distinct={definition.distinct}
                    value={val ? { [labelkey]: val } : null}
                    onChange={(v) => {
                        addFilter(field, v?.[labelkey] ?? null, definition.operator || 'equals');
                    }}
                    style={FILTER_STYLE}
                    size={FILTER_SIZE}
                    ctaText=" "
                />
            );
        }
        default:
            return null;
    }
};

export const DashboardFilterRenderer = ({ filterId, t, filterModel, addFilter, filterCatalog, visibilityUser }) => {
    const staticOptions = useDashboardFilterStaticPresets(t);
    const catalogEntry = filterCatalog?.[filterId];
    if (catalogEntry && typeof catalogEntry === 'object' && catalogEntry.type) {
        return (
            <DashboardConfigurableFilter
                definition={catalogEntry}
                t={t}
                filterModel={filterModel}
                addFilter={addFilter}
                staticOptions={staticOptions}
                visibilityUser={visibilityUser}
            />
        );
    }
    return null;
};
